import React, { createRef, useEffect, useState } from 'react'
import {
  InvalidEmailInformation,
  LoginContent,
  LoginSubTitle,
  LoginTitle,
  RequestDemoLink,
} from '@features/LoginModal/styles'
import InputText from '@elements/InputText'
import { Button } from '@thriveglobal/thrive-ui-lib'
import { COLORS } from '@enums/colors'
import { useTranslation } from 'react-i18next'
import useAuth from '@hooks/use-auth'
import { SegmentPage } from '@enums/segment'
import { trackCustomPageEvent } from '@api/segment/page'

interface Props {
  email: string
  transition: () => void
  onClose: () => void
}

const PasswordFailure: React.FC = () => {
  const { t } = useTranslation()
  return (
    <InvalidEmailInformation>
      {t('login_modal_incorrect_password')}{' '}
    </InvalidEmailInformation>
  )
}

const LoginModalPassword = ({ email, transition, onClose }: Props) => {
  const inputRef = createRef<HTMLInputElement>()
  const { t } = useTranslation()
  const { passwordSignIn, isLoading } = useAuth()
  const [validSignIn, setValidSignIn] = useState(true)

  useEffect(() => trackCustomPageEvent(SegmentPage.enterPassword), []);

  const onSubmit = async () => {
    const password = inputRef.current?.value
    if (password) {
      try {
        await passwordSignIn(email, inputRef.current?.value || '')
        onClose()
      } catch (error) {
        setValidSignIn(false)
      }
    }
  }

  const onForgotPassword = () => {
    transition()
  }

  return (
    <LoginContent>
      <LoginTitle>{t('login_modal_title_password')}</LoginTitle>
      <LoginSubTitle>{t('login_modal_sub_title_password')}</LoginSubTitle>
      <InputText
        ref={inputRef}
        placeholder={t('login_modal_placeholder_password')}
        onSubmit={onSubmit}
        type={'password'}
        isValid={validSignIn}
        data-testid="modal-input"
        InvalidInfo={validSignIn ? null : PasswordFailure}
      />
      <Button data-testid="modal-submit" loading={isLoading} onClick={onSubmit}>
        {t('login_modal_button_password')}
      </Button>
      <RequestDemoLink
        data-testid="modal-secondary-btn"
        onClick={onForgotPassword}
        color={COLORS.teal}
      >
        {t('login_modal_forgotten_password')}
      </RequestDemoLink>
    </LoginContent>
  )
}

export default LoginModalPassword
